import { put, call, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
    IFetchForgotPasswordAction,
    IFetchChangePasswordAction,
    IFetchResetPasswordAction,
    LoginPayload,
    IFetchGoogleLoginAction,
} from '../redux/interface';
import { loginActions } from '../redux/loginSlice';
import {
    fetchForgotPasswordApi,
    fetchLoginApi,
    fetchChangePasswordApi,
    fetchResetPasswordApi,
    fetchGoogleLoginApi,
} from '../../apis/login';
import { RoutePaths } from '../../../routes/RoutePaths';
import { SnackbarUtils } from '../../../common';
import { userActions } from '../../user/redux/userSlice';
import {
    globalNavigate,
    EnvTypes,
    logout as logoutUtils,
    saveTokens,
    isToolsPage,
} from '../../../base';
import {
    fetchCompanyBaseUsersSaga,
    fetchMyUserSaga,
} from '../../user/saga/saga';
import i18next from 'i18next';
import { fetchTrackableSourceSaga } from '../../jobs/saga/saga';
import Cookies from 'js-cookie';
import { organizationActions } from '../../organization/redux';
import {
    fetchCompanySettingsSaga,
    fetchTagsSaga,
} from '../../organization/saga/saga';

export function* fetchLoginSaga(action: PayloadAction<LoginPayload>) {
    try {
        const { email, password, isTools } = action.payload;
        if (process.env.VITE_ENV_TYPE === EnvTypes.LOCAL) {
            Cookies.set('isTools', isTools ? 'true' : 'false');
        }
        const { response, error } = yield call(fetchLoginApi, {
            email,
            password,
        });
        if (response?.status === 200) {
            yield put(loginActions.setLogin({ isLoggedIn: true }));
            saveTokens(response.data?.access, response.data?.refresh);
            yield put(userActions.fetchMyUser());
            if (isToolsPage()) {
                globalNavigate(RoutePaths.chat);
            } else {
                globalNavigate(RoutePaths.dashboard);
            }
        } else if (error?.status === 401) {
            yield put(loginActions.setLoginFailed());
            yield call(
                SnackbarUtils.error,
                i18next.t('loginPage.wrongEmailOrPassword')
            );
        } else {
            yield put(loginActions.setLoginFailed());
            yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    }
}

export function* fetchForgotPasswordSaga(
    action: PayloadAction<IFetchForgotPasswordAction>
) {
    try {
        const { payload } = action;
        const { response } = yield call(fetchForgotPasswordApi, payload);
        if (response?.status === 200) {
            yield put(
                loginActions.setResetPasswordEmailSent({
                    resetPasswordEmailSent: true,
                })
            );
        }
    } catch (error) {
        console.log(error);
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    } finally {
        yield put(loginActions.setForgotPasswordDone());
    }
}
export function* fetchChangePasswordSaga(
    action: PayloadAction<IFetchChangePasswordAction>
) {
    try {
        const { payload } = action;
        const { response } = yield call(fetchChangePasswordApi, payload);
        if (response?.status === 200) {
            yield put(loginActions.setChangePasswordDone);
        } else {
            yield put(loginActions.setChangePasswordDone);
        }
    } catch (error) {
        console.log(error);
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    }
}
export function* fetchResetPasswordSaga(
    action: PayloadAction<IFetchResetPasswordAction>
) {
    try {
        const { payload } = action;
        const { response } = yield call(fetchResetPasswordApi, payload);
        if (response?.status === 200) {
            yield put(loginActions.setResetPasswordDone);
            globalNavigate(RoutePaths.login);
        } else {
            yield put(loginActions.setResetPasswordDone);
        }
    } catch (error) {
        console.log(error);
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    }
}

export function* fetchInitialDataSaga() {
    try {
        yield all([
            call(fetchMyUserSaga),
            call(fetchCompanyBaseUsersSaga),
            call(fetchTrackableSourceSaga),
            call(fetchTagsSaga),
            call(fetchCompanySettingsSaga),
        ]);
    } catch (error) {
        console.log(error);
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    } finally {
        yield put(loginActions.setFetchInitialDataDone());
    }
}

export function* fetchGoogleLoginSaga(
    action: PayloadAction<IFetchGoogleLoginAction>
) {
    try {
        const { payload } = action;
        const { response } = yield call(fetchGoogleLoginApi, payload);
        if (response?.status === 200) {
            saveTokens(response.data?.access, response.data?.refresh);
            yield put(userActions.fetchMyUser());
            globalNavigate(RoutePaths.dashboard);
        } else {
            yield call(SnackbarUtils.error, response.data?.error?.message?.[0]);
        }
    } catch (error) {
        console.log(error);
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    } finally {
        yield put(loginActions.setGoogleLoginDone());
    }
}

export function* logoutSaga() {
    try {
        logoutUtils();
        globalNavigate(RoutePaths.login);
    } catch (error) {
        console.log(error);
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    }
}
